import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Coffee, User, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { ColorChoiceGrid } from '../common/ColorChoice';

function RegisterPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [inviteData, setInviteData] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [inviteError, setInviteError] = useState('');

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [color, setColor] = useState(null);

  useEffect(() => {
    const validateToken = async () => {
      try {
        const res = await api.get(`/auth/invite/${token}`);
        setInviteData(res.data);
      } catch (err) {
        setInviteError(err.response?.data?.message || 'Invalid or expired invite link.');
      } finally {
        setLoadingInvite(false);
      }
    };
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!color && inviteData?.color_options?.length) {
      setError('Please pick your color.');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/auth/register/${token}`, { name: name.trim(), password, color });
      toast.success('Account created! Signing you in…');
      await login(inviteData.email, password);
      navigate('/roster');
    } catch (err) {
      // Colour taken meanwhile → fresh options to choose from
      if (err.response?.data?.color_options) {
        setInviteData((d) => ({ ...d, color_options: err.response.data.color_options }));
        setColor(null);
      }
      setError(err.response?.data?.error || err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingInvite) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--primary)' }}>
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  if (inviteError) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, var(--primary-light) 0%, var(--primary) 50%, var(--primary-dark) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-xl)', padding: '40px', maxWidth: '420px', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ fontWeight: 700, marginBottom: '12px' }}>Invalid Invite</h2>
          <p style={{ color: 'var(--text-light)', marginBottom: '24px' }}>{inviteError}</p>
          <a href="/login" className="btn btn-primary">Go to login</a>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, var(--primary-light) 0%, var(--primary) 50%, var(--primary-dark) 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        style={{
          background: 'var(--white)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-lg)',
          padding: '40px',
          width: '100%',
          maxWidth: '420px',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '56px', height: '56px', borderRadius: '50%', background: 'var(--primary)', marginBottom: '12px',
            }}
          >
            <Coffee size={28} color="var(--text)" />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '6px' }}>
            Join Leuchtstoff
          </h1>
          <p style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
            You've been invited to join the team!
          </p>
        </div>

        {/* Invited email display */}
        <div
          style={{
            background: 'var(--primary-xlight)',
            border: '1px solid var(--primary)',
            borderRadius: 'var(--radius)',
            padding: '10px 14px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <CheckCircle size={16} color="var(--primary-dark)" />
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>Invited email</div>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{inviteData?.email}</div>
          </div>
          {inviteData?.role && (
            <span className={`badge badge-${inviteData.role === 'admin' ? 'admin' : 'user'}`} style={{ marginLeft: 'auto' }}>
              {inviteData.role}
            </span>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div
              style={{
                background: 'var(--danger-light)', color: 'var(--danger)',
                padding: '10px 14px', borderRadius: 'var(--radius)',
                fontSize: '0.875rem', marginBottom: '16px', border: '1px solid #ffcdd2',
              }}
            >
              {error}
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="name">Full name</label>
            <div style={{ position: 'relative' }}>
              <User
                size={16}
                style={{
                  position: 'absolute', left: '12px', top: '50%',
                  transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none',
                }}
              />
              <input
                id="name"
                type="text"
                className="form-input"
                placeholder="Your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ paddingLeft: '38px' }}
                autoComplete="name"
                autoFocus
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">Password</label>
            <div style={{ position: 'relative' }}>
              <Lock
                size={16}
                style={{
                  position: 'absolute', left: '12px', top: '50%',
                  transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none',
                }}
              />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ paddingLeft: '38px', paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={{
                  position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px',
                }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="confirm">Confirm password</label>
            <div style={{ position: 'relative' }}>
              <Lock
                size={16}
                style={{
                  position: 'absolute', left: '12px', top: '50%',
                  transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none',
                }}
              />
              <input
                id="confirm"
                type="password"
                className="form-input"
                placeholder="Repeat your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{ paddingLeft: '38px' }}
              />
            </div>
          </div>

          {inviteData?.color_options?.length > 0 && (
            <div className="form-group">
              <label className="form-label">Your color</label>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginBottom: '8px' }}>
                It marks your shifts and your name in the roster. You choose it once — later only an admin can change it.
              </p>
              <ColorChoiceGrid options={inviteData.color_options} value={color} onChange={setColor} name={name} />
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary w-full btn-lg"
            disabled={loading}
            style={{ justifyContent: 'center' }}
          >
            {loading ? (
              <>
                <div className="spinner" style={{ width: '18px', height: '18px', borderWidth: '2px' }} />
                Creating account…
              </>
            ) : (
              'Create account'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default RegisterPage;
